
import { DocumentViewModel } from '../ViewModels';
import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useLayoutLifeCycle,
} from '@/Shared';

import { DocumentHeader } from '../document-config';
import { useDocumentEnricher } from '../Hooks';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useDocument } from '@/OneLiners';
import { DocumentModel } from '../DocumentModel';

/**
 * useDocumentTableConfig hook
 *
 * Domain-specific layout composition hook for Document entities.
 * Acts as the composition layer between:
 * - Document domain data access,
 * - enrichment logic,
 * - permission state aggregation,
 * - and the generic layout lifecycle infrastructure.
 *
 * Responsibilities:
 * - Aggregate domain and enrichment readiness states into a single contract.
 * - Provide dynamic header configuration for the Document table.
 * - Define strict Model → ViewModel transformation.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<DocumentViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<DocumentViewModel>
 */
export function useDocumentTableConfig(props: DomainHookProps): DomainTableConfig<DocumentViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Document;

    /**
     * Domain enums used for headers and VM mapping.
     */
    const { DocumentEnums } = useAppEnum();

    /**
     * Document domain entry point.
     */
    const documentHook = useDocument();

    /**
     * Document enrichment layer.
     *
     * Resolves derived fields or cross-domain relationships.
     */
    const {
        getStrictEnrichedDocument,
        enricherResponse,
    } = useDocumentEnricher();

    /**
     * Combined readiness response.
     *
     * The layout lifecycle initializes only once this
     * contract reaches a succeeded state.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            documentHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: documentHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
    * Delegation to generic layout lifecycle.
    */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: documentHook,

        /**
         * Header configuration for Property table.
         */
        headers: DocumentHeader.build(DocumentEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            document: DocumentModel, isDisplaying: boolean) =>
            new DocumentViewModel({
                document: getStrictEnrichedDocument(document),
                viewEnums: DocumentEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}